import {Button, IconButton, LinearProgress, Stack, Typography, useTheme} from '@mui/material';
import React, {useEffect, useId, useRef, useState} from 'react';

import {AlertActions, AlertInfo, AlertModal, IconIllustration} from 'presentation/components';
import {Drawer} from 'presentation/components/Drawer';
import Icon from 'presentation/icons';
import {readableSize} from 'presentation/utils';

import {
	ACCEPT_ATTR,
	ALLOWED_UPLOAD_EXTENSIONS,
	MAX_SIZE_BYTES,
	SEND_DOCUMENT_DRAWER,
} from '../../constants';
import {DropZone, HiddenInput, UploadedFileCard} from './styles';
import {SendDocumentDrawerProps} from './types';

type UploadStatus = 'uploading' | 'done' | 'error';

// TODO: substituir por progresso real do POST (axios onUploadProgress) quando endpoint existir.
const FAKE_PROGRESS_STEP_MS = 200;
const FAKE_PROGRESS_INCREMENT = 12;
// TODO: remover simulação de falha — só serve pra QA do estado de erro enquanto mock.
const FAKE_FAILURE_CHANCE = 0.3;

function validateFile(file: File): string | undefined {
	const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
	if (!ALLOWED_UPLOAD_EXTENSIONS.includes(extension)) return 'Tipo de arquivo não suportado';
	if (file.size > MAX_SIZE_BYTES) return 'Arquivo excede 10MB';
	return undefined;
}

export function SendDocumentDrawer({open, row, onClose, onSave}: SendDocumentDrawerProps) {
	const inputId = useId();
	const inputRef = useRef<HTMLInputElement>(null);
	const progressIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
	const theme = useTheme();

	const [file, setFile] = useState<File | null>(null);
	const [fileError, setFileError] = useState<string | undefined>(undefined);
	const [isDragging, setIsDragging] = useState(false);
	const [uploadStatus, setUploadStatus] = useState<UploadStatus>('uploading');
	const [progress, setProgress] = useState(0);
	const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

	const clearProgressInterval = () => {
		if (progressIntervalRef.current) {
			clearInterval(progressIntervalRef.current);
			progressIntervalRef.current = null;
		}
	};

	const resetState = () => {
		clearProgressInterval();
		setFile(null);
		setFileError(undefined);
		setIsDragging(false);
		setUploadStatus('uploading');
		setProgress(0);
		setIsDeleteConfirmOpen(false);
	};

	useEffect(() => {
		if (!open) resetState();
		return () => clearProgressInterval();
	}, [open]);

	const startFakeUpload = () => {
		setUploadStatus('uploading');
		setProgress(0);
		clearProgressInterval();
		const shouldFail = Math.random() < FAKE_FAILURE_CHANCE;
		const failureAt = shouldFail
			? Math.floor(Math.random() * 60) + 20 // falha entre 20% e 80%
			: Infinity;
		progressIntervalRef.current = setInterval(() => {
			setProgress(prev => {
				const nextValue = prev + FAKE_PROGRESS_INCREMENT;
				if (nextValue >= failureAt) {
					clearProgressInterval();
					setUploadStatus('error');
					return prev;
				}
				if (nextValue >= 100) {
					clearProgressInterval();
					setUploadStatus('done');
					return 100;
				}
				return nextValue;
			});
		}, FAKE_PROGRESS_STEP_MS);
	};

	const handleFiles = (incoming: FileList | null) => {
		if (!incoming || incoming.length === 0) return;
		const next = incoming[0];
		const error = validateFile(next);
		setFile(next);
		setFileError(error);
		if (error) {
			clearProgressInterval();
			setUploadStatus('uploading');
			setProgress(0);
			return;
		}
		startFakeUpload();
	};

	const handleDrop = (event: React.DragEvent<HTMLLabelElement>) => {
		event.preventDefault();
		setIsDragging(false);
		handleFiles(event.dataTransfer.files);
	};

	const handleDragOver = (event: React.DragEvent<HTMLLabelElement>) => {
		event.preventDefault();
		setIsDragging(true);
	};

	const handleDragLeave = () => setIsDragging(false);

	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		handleFiles(event.target.files);
		event.target.value = '';
	};

	const handleRequestRemove = () => {
		setIsDeleteConfirmOpen(true);
	};

	const handleConfirmRemove = () => {
		clearProgressInterval();
		setFile(null);
		setFileError(undefined);
		setUploadStatus('uploading');
		setProgress(0);
		setIsDeleteConfirmOpen(false);
	};

	const handleCancelRemove = () => {
		setIsDeleteConfirmOpen(false);
	};

	const handleSave = () => {
		if (!file || fileError || uploadStatus !== 'done' || !row) return;
		onSave(file, row);
	};

	const canSave = Boolean(file && !fileError && uploadStatus === 'done');
	const showDropZone = !file || Boolean(fileError);
	const showUploadedCard = Boolean(file && !fileError);

	return (
		<Drawer.Root open={open} onClose={onClose} onOpen={() => undefined} width="600px">
			<Drawer.Header title={row?.documento ?? ''} onClose={onClose} />
			<Drawer.Content sx={{display: 'flex', flexDirection: 'column', gap: 2, p: 2}}>
				<AlertInfo.Root severity="info" icon={<Icon name="info-outlined" />}>
					<AlertInfo.Title title={SEND_DOCUMENT_DRAWER.alert.title} />
					<AlertInfo.Description>
						{SEND_DOCUMENT_DRAWER.alert.description}
					</AlertInfo.Description>
				</AlertInfo.Root>

				{showDropZone ? (
					<DropZone
						htmlFor={inputId}
						$isDragging={isDragging}
						$hasError={Boolean(fileError)}
						onDrop={handleDrop}
						onDragOver={handleDragOver}
						onDragLeave={handleDragLeave}>
						<IconIllustration
							customIconName="upload-file-filled"
							variant={fileError ? 'error' : 'primary'}
							size="custom"
						/>
						<Typography variant="subtitle1" color="primary" fontWeight={600}>
							{SEND_DOCUMENT_DRAWER.upload.title}
						</Typography>
						{fileError ? (
							<Typography variant="body2" color="error" textAlign="center">
								{fileError}
							</Typography>
						) : null}
						<Typography
							variant="body2"
							color={fileError ? 'error' : 'text.secondary'}
							textAlign="center">
							{SEND_DOCUMENT_DRAWER.upload.description}
						</Typography>
						<HiddenInput
							id={inputId}
							ref={inputRef}
							type="file"
							accept={ACCEPT_ATTR}
							onChange={handleChange}
						/>
					</DropZone>
				) : null}

				{showUploadedCard && file ? (
					<UploadedFileCard>
						<Stack direction="row" gap={2} alignItems="center">
							{uploadStatus === 'done' ? (
								<Icon
									name="check-circle-filled"
									size={40}
									color={theme.palette.success.main}
								/>
							) : (
								<IconIllustration
									customIconName="upload-file-filled"
									variant={uploadStatus === 'error' ? 'error' : 'primary'}
									size="custom"
								/>
							)}
							<Stack flex={1} minWidth={0} gap={0}>
								{uploadStatus === 'error' ? (
									<>
										<Typography variant="body2" color="error">
											{SEND_DOCUMENT_DRAWER.uploadError.title}
										</Typography>
										<Button
											type="button"
											variant="text"
											size="small"
											onClick={startFakeUpload}
											sx={{
												alignSelf: 'flex-start',
												p: 0,
												minWidth: 0,
												textTransform: 'none',
											}}>
											{SEND_DOCUMENT_DRAWER.uploadError.retry}
										</Button>
									</>
								) : (
									<>
										<Typography variant="body2" color="text.primary" noWrap>
											{file.name}
										</Typography>
										<Typography variant="caption" color="text.secondary">
											{`${readableSize(file.size)}${
												uploadStatus === 'uploading'
													? ` • ${SEND_DOCUMENT_DRAWER.uploadingLabel}`
													: ''
											}`}
										</Typography>
									</>
								)}
							</Stack>
							<IconButton
								size="small"
								aria-label="Remover"
								onClick={handleRequestRemove}>
								<Icon name="close-default" />
							</IconButton>
						</Stack>
						{uploadStatus === 'uploading' ? (
							<LinearProgress variant="determinate" value={progress} />
						) : null}
					</UploadedFileCard>
				) : null}
			</Drawer.Content>

			<Stack
				direction="row"
				gap={1.5}
				p={2}
				sx={{borderTop: t => `1px solid ${t.palette.divider}`}}>
				<Button type="button" variant="outlined" fullWidth size="large" onClick={onClose}>
					{SEND_DOCUMENT_DRAWER.cancel}
				</Button>
				<Button
					type="button"
					variant="contained"
					fullWidth
					size="large"
					disabled={!canSave}
					onClick={handleSave}>
					{SEND_DOCUMENT_DRAWER.save}
				</Button>
			</Stack>

			<AlertModal
				open={isDeleteConfirmOpen}
				onClose={handleCancelRemove}
				headerTitle={SEND_DOCUMENT_DRAWER.deleteConfirm.headerTitle}
				variant="info"
				customIconName="info-filled"
				title={SEND_DOCUMENT_DRAWER.deleteConfirm.title}
				description={SEND_DOCUMENT_DRAWER.deleteConfirm.description}
				footerContent={
					<AlertActions
						primaryAction={handleConfirmRemove}
						secondaryAction={handleCancelRemove}
						primaryText={SEND_DOCUMENT_DRAWER.deleteConfirm.confirm}
						secondaryText={SEND_DOCUMENT_DRAWER.deleteConfirm.cancel}
						flexDirection="column-reverse"
					/>
				}
			/>
		</Drawer.Root>
	);
}
